import type { SyncController } from "../offline/SyncContext";
import { skipWaitingMessage } from "../sw/messages";

/**
 * State of an application update:
 * - `none`: no new version is waiting;
 * - `available`: a new version is installed and waits for activation;
 * - `waitingForSync`: activation was requested and starts when the running sync ends;
 * - `activating`: the new version was asked to take over; the page reloads afterwards.
 */
export type UpdateState = "none" | "available" | "waitingForSync" | "activating";

/** The part of a `ServiceWorker` the updater uses. */
export interface WorkerPort {
  readonly state: string;
  postMessage(message: unknown): void;
  addEventListener(type: "statechange", listener: () => void): void;
}

/** The part of a `ServiceWorkerRegistration` the updater uses. */
export interface RegistrationPort {
  readonly installing: WorkerPort | null;
  readonly waiting: WorkerPort | null;
  update(): Promise<unknown>;
  addEventListener(type: "updatefound", listener: () => void): void;
}

/** The part of the `ServiceWorkerContainer` (`navigator.serviceWorker`) the updater uses. */
export interface ServiceWorkerHost {
  readonly controller: object | null;
  register(scriptUrl: string, options: { readonly scope: string }): Promise<RegistrationPort>;
  addEventListener(type: "controllerchange", listener: () => void): void;
}

/** URL of the built service worker (`src/sw/serviceWorker.ts`, see `vite.config.ts`). */
export const serviceWorkerScriptUrl = "/serviceWorker.js";

/** What the UI needs from the update mechanism. */
export interface AppUpdateController {
  /** Returns the current state. */
  readonly getState: () => UpdateState;
  /** Subscribes to state changes. */
  readonly subscribe: (listener: () => void) => () => void;
  /** Requests the activation of the waiting version. */
  readonly activate: () => void;
}

/** Dependencies of the {@link ServiceWorkerUpdater}. */
export interface ServiceWorkerUpdaterOptions {
  readonly host: ServiceWorkerHost;
  /** URL of the service worker script. */
  readonly scriptUrl: string;
  /** Status of the synchronization; activation waits while it runs (spec 11.4). */
  readonly sync: Pick<SyncController, "getStatus" | "subscribe">;
  /** Reloads the page after a new version took control. */
  readonly reload: () => void;
}

/**
 * Update strategy of the PWA (spec 11.4): a new version is installed in the background, the UI
 * shows a hint, and the version is activated only on request and only while no synchronization
 * runs. When a new version takes control (from this or another tab) the page reloads once, so
 * the code in memory matches the precached files.
 */
export class ServiceWorkerUpdater implements AppUpdateController {
  private readonly options: ServiceWorkerUpdaterOptions;

  private readonly listeners = new Set<() => void>();

  private state: UpdateState = "none";

  private registration: RegistrationPort | null = null;

  private stopWaitingForSync: (() => void) | null = null;

  private reloading = false;

  /**
   * Creates the updater.
   *
   * @param options - Dependencies.
   */
  constructor(options: ServiceWorkerUpdaterOptions) {
    this.options = options;
  }

  /** @inheritdoc */
  readonly getState = (): UpdateState => this.state;

  /** @inheritdoc */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    const unsubscribe = (): void => {
      this.listeners.delete(listener);
    };

    return unsubscribe;
  };

  /** @inheritdoc */
  readonly activate = (): void => {
    if (this.state !== "available") {
      return;
    }

    const { sync } = this.options;

    if (sync.getStatus().phase !== "syncing") {
      this.sendSkipWaiting();
      return;
    }

    this.setState("waitingForSync");
    this.stopWaitingForSync = sync.subscribe((status) => {
      if (status.phase !== "syncing") {
        this.stopWaitingForSync?.();
        this.stopWaitingForSync = null;
        this.sendSkipWaiting();
      }
    });
  };

  /**
   * Registers the service worker and starts observing updates.
   *
   * @returns A promise that resolves once the registration exists.
   */
  async start(): Promise<void> {
    const { host } = this.options;
    // Without a controller this is the first installation: its takeover needs no reload.
    const hadController = host.controller !== null;

    host.addEventListener("controllerchange", () => {
      if (hadController && !this.reloading) {
        this.reloading = true;
        this.options.reload();
      }
    });

    const registration = await host.register(this.options.scriptUrl, { scope: "/" });
    this.registration = registration;

    if (registration.waiting !== null && host.controller !== null) {
      this.setState("available");
    }

    registration.addEventListener("updatefound", () => {
      const installing = registration.installing;

      if (installing === null) {
        return;
      }

      installing.addEventListener("statechange", () => {
        if (installing.state === "installed" && host.controller !== null) {
          this.setState("available");
        }
      });
    });
  }

  /**
   * Asks the browser to look for a new version. Failures (offline) are ignored; the next check
   * tries again.
   *
   * @returns A promise that resolves when the check has finished.
   */
  async checkForUpdate(): Promise<void> {
    const registration = this.registration;

    if (registration === null) {
      return;
    }

    try {
      await registration.update();
    } catch {
      // Offline or server unreachable: the next trigger checks again.
    }
  }

  private sendSkipWaiting(): void {
    const waiting = this.registration?.waiting ?? null;

    if (waiting === null) {
      this.setState("none");
      return;
    }

    this.setState("activating");
    waiting.postMessage(skipWaitingMessage);
  }

  private setState(state: UpdateState): void {
    if (this.state === state) {
      return;
    }

    this.state = state;

    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * Creates a controller for environments without service worker (development server, unsupported
 * browsers, tests): no update is ever available.
 *
 * @returns The controller.
 */
export function createInactiveUpdateController(): AppUpdateController {
  const controller: AppUpdateController = {
    getState: () => "none",
    subscribe: () => () => undefined,
    activate: () => undefined,
  };

  return controller;
}

/** Browser objects the update checks listen to (injected for tests). */
export interface UpdateCheckTargets {
  readonly window: Pick<Window, "setInterval" | "clearInterval">;
  readonly document: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState">;
}

/** Interval of the periodic update check. */
export const updateCheckIntervalMs = 3_600_000;

/**
 * Checks for a new version whenever the application becomes visible and hourly.
 *
 * @param updater - The updater.
 * @param targets - Window and document.
 * @param intervalMs - Interval of the periodic check.
 * @returns A function that stops the checks.
 */
export function startUpdateChecks(
  updater: Pick<ServiceWorkerUpdater, "checkForUpdate">,
  targets: UpdateCheckTargets,
  intervalMs: number = updateCheckIntervalMs,
): () => void {
  const check = (): void => {
    void updater.checkForUpdate();
  };
  const onVisibilityChange = (): void => {
    if (targets.document.visibilityState === "visible") {
      check();
    }
  };
  const interval = targets.window.setInterval(check, intervalMs);

  targets.document.addEventListener("visibilitychange", onVisibilityChange);

  const stop = (): void => {
    targets.window.clearInterval(interval);
    targets.document.removeEventListener("visibilitychange", onVisibilityChange);
  };

  return stop;
}
